import { getReference } from '@atc/bonnet-reference';
import constants from './constants';
import normalizeSeoValue from './normalizeSeoValue';
import decodeLocation from './decodeLocation';
async function getCodeFromReference(value, _ref) {
  let {
    allowedCharacters = ["'"],
    disallowedCharacters,
    param,
    ...rest
  } = _ref;
  const {
    payload
  } = await getReference(param, rest);
  const parsedValue = normalizeSeoValue(value, allowedCharacters, disallowedCharacters);

  // Check if we need canonical matching (when allowed chars might be used as delimiters)
  // e.g., '+' is both allowed and historically used as a space delimiter
  const needsCanonicalMatching = allowedCharacters.some(char => value.includes(char));
  if (needsCanonicalMatching) {
    // Use the first matching allowed character as canonical space representation
    const canonicalChar = allowedCharacters.find(char => value.includes(char));
    const canonicalValue = parsedValue.replace(/\s+/g, canonicalChar);

    // Match by converting all spaces to the canonical character
    const match = payload.find(item => {
      const parsedName = normalizeSeoValue(item.name, allowedCharacters, disallowedCharacters);
      const canonicalName = parsedName.replace(/\s+/g, canonicalChar);
      return canonicalName === canonicalValue;
    });
    return match === null || match === void 0 ? void 0 : match.code;
  }

  // Standard matching when no ambiguous characters
  const match = payload.find(item => {
    const parsedName = normalizeSeoValue(item.name, allowedCharacters, disallowedCharacters);
    return parsedName === parsedValue;
  }) || {};
  return match.code;
}
export default async function decode(value) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    param,
    queryZip
  } = options;
  let decodedValue;
  switch (param) {
    // handle all numeric seo param types
    case 'dealerId':
    case 'listingId':
      {
        // ensure the value is at least 1 number and then set the decodedValue as a string
        // We assume the length check for each has already been handled
        const re = new RegExp(/\d+/);
        if (re.test(value)) {
          decodedValue = `${value}`;
        }
        break;
      }
    case 'listingType':
      {
        // listingType may have extraneous seo text we need to remove
        // before we can match against any reference data
        const _value = value.toLowerCase().replace(constants.CARS, '');
        decodedValue = getCodeFromReference(_value, options);
        break;
      }
    case 'modelCode':
      {
        decodedValue = getCodeFromReference(value, {
          allowedCharacters: ['+'],
          ...options
        });
        break;
      }
    case 'trimCode':
      {
        decodedValue = getCodeFromReference(value, {
          allowedCharacters: ['+'],
          disallowedCharacters: ["'"],
          ...options
        });
        break;
      }
    case 'location':
      {
        // location is stored as a string but needs to be decoded as a city, state, zip object
        const parsedValue = normalizeSeoValue(value);
        decodedValue = await decodeLocation({
          parsedValue,
          queryZip
        });
        break;
      }
    case 'mpgRange':
      {
        // normalize seo string case and delimiters
        const parsedValue = normalizeSeoValue(value);
        const matches = parsedValue.match(/(\d+)/gi);
        if (matches.length === 1) {
          // ex: '40-'
          decodedValue = `${matches[0]}-`;
        } else {
          // ex: '20-29'
          decodedValue = matches.join('-');
        }
        break;
      }
    case 'price':
      {
        // price is stored as a string but needs to be decoded as a minPrice and maxPrice object
        const parsedValue = normalizeSeoValue(value);
        const comparators = `${constants.RANGE}|${constants.OVER}|${constants.UNDER}`;
        const pattern = new RegExp(`(?:${constants.CARS})\\s+(${comparators})\\s?(\\d+)(?:\\s?(?:${constants.CONNECTOR})\\s?(\\d+)?)?`);
        const match = parsedValue.match(pattern);

        // create object to store decoded values
        decodedValue = {};
        if (match) {
          // eslint-disable-next-line no-unused-vars
          const [input, comparator, price1, price2] = match;
          switch (comparator) {
            case 'between':
              decodedValue.minPrice = price1;
              decodedValue.maxPrice = price2;
              break;
            case 'under':
              decodedValue.maxPrice = price1;
              break;
            case 'over':
              decodedValue.minPrice = price1;
              break;
            default:
          }
        }
        break;
      }
    case 'year':
      {
        decodedValue = {};

        // ensure year is a 4 digit number
        const re = new RegExp(/\d{4}/);
        if (re.test(value)) {
          decodedValue = {
            startYear: `${value}`,
            endYear: `${value}`
          };
        }
        break;
      }
    case 'moneyBackGuarantee':
      {
        decodedValue = {
          vehicleExchange: 'true'
        };
        break;
      }
    case 'commercialVehicles':
      {
        decodedValue = {
          vehicleUseType: 'COMMERCIAL'
        };
        break;
      }
    default:
      decodedValue = getCodeFromReference(value, options);
  }
  return decodedValue;
}